import React, { useEffect } from "react";
import styles from "../../assets/styles/layout.module.css";
import { Outlet } from "react-router-dom";
import User from "../User/User";
import ThriveBreadcrumb from "./ThriveBreadcrumb";
import Footer from "./Footer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import {
  DASHBOARD_SIDEBAR_ID,
  NAV_DRAWER_ID,
  useNavbarContext,
} from "../../context/NavbarContext";
import { useLocation } from "react-router-dom";
import { Box, Drawer } from "@mui/material";
import NavBar from "../Dashboard/NavBar";
import {
  NAV_DRAWER_QUERY,
  TOUCH_QUERY,
  useViewport,
} from "../../utils/useViewport";

// Logged-out pages: login, surveys, unsubscribe and set-password get no nav
// and no breadcrumb.
const isPublicPath = (path: string) =>
  path === "/" || /^\/(survey|unsubscribe|set-password)(\/|$)/.test(path);

const Layout = () => {
  const { isNavbarOpen, setIsNavbarOpen, isDrawerOpen, setIsDrawerOpen } =
    useNavbarContext();
  const { navIsDrawer } = useViewport();
  const location = useLocation();
  const currentPath = location.pathname;
  const showNav = !isPublicPath(currentPath);

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location, setIsDrawerOpen]);

  // /dashboard pages at >= 1024 px have the sidebar column; everywhere else
  // the menu button opens the nav drawer.
  const togglesSidebar = /^\/dashboard(\/|$)/.test(currentPath) && !navIsDrawer;
  const handleMenuClick = () => {
    if (togglesSidebar) {
      setIsNavbarOpen(!isNavbarOpen);
    } else {
      setIsDrawerOpen(true);
    }
  };
  const menuExpanded = togglesSidebar ? isNavbarOpen : isDrawerOpen;
  const menuTarget = togglesSidebar ? DASHBOARD_SIDEBAR_ID : NAV_DRAWER_ID;

  // Any nav link closes the drawer, including the ones that open a new tab
  // and would otherwise leave it open on this page.
  const closeDrawerOnLinkClick = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest("a")) setIsDrawerOpen(false);
  };
  return (
    <div className={styles.application}>
      <div className={styles.header_container}>
        <div className={styles.header}>
          <div className={styles.user_container}>
            <User />
          </div>
        </div>
      </div>
      <Box
        className="flex ml-[30px]"
        sx={{
          maxWidth: "1440px",
          margin: "auto",
          paddingLeft: "6px",
          // Centre the breadcrumb on the taller touch-size menu button.
          [`@media ${TOUCH_QUERY}`]: { alignItems: "center" },
        }}
      >
        {/* zIndex: below 1024 px some pages pull their first row up beside
            the breadcrumb (made for the sidebar layout); the menu button stays
            on top. aria-controls only while the nav it names is rendered. */}
        {showNav && (
          <IconButton
            onClick={handleMenuClick}
            color="inherit"
            aria-label="Open Menu"
            aria-expanded={menuExpanded}
            aria-controls={menuExpanded ? menuTarget : undefined}
            sx={{
              [`@media ${NAV_DRAWER_QUERY}`]: { zIndex: 1 },
              [`@media ${TOUCH_QUERY}`]: { p: 1.5 },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {showNav && <ThriveBreadcrumb />}
      </Box>

      <div className={styles.content_container}>
        <Outlet />
      </div>
      <Footer></Footer>
      {showNav && (
        <Drawer
          anchor="left"
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          PaperProps={{
            component: "nav",
            id: NAV_DRAWER_ID,
            "aria-label": "Main navigation",
            onClick: closeDrawerOnLinkClick,
            sx: { width: 280, maxWidth: "85vw" },
          }}
        >
          <NavBar />
        </Drawer>
      )}
    </div>
  );
};

export default Layout;
